import { getOrderStatusLabel } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalOrderService from '../services/portalOrderService';
import { CardChevron } from './CardChevron';
import { RecentListRowsSkeleton } from './skeletons';

/**
 * Account-home recent orders (PD-14080 review feedback): latest three with
 * a View all link — sits in the recent-activity row beside invoices and
 * transactions.
 */
export function RecentOrdersCard({
	title,
}: {
	title?: string;
} = {}): ReactElement | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { token, activeClientId } = usePortalAuth();
	const display = useDisplaySettings();

	const { data, isLoading } = useQuery({
		// Key on the active client so the card refetches on an account
		// switch (the recent lists are scoped to the active client).
		queryKey: ['portal-orders-recent', activeClientId],
		queryFn: () =>
			portalOrderService.getOrders(token ?? '', { page: 1, limit: 3 }),
		enabled: token != null,
		staleTime: 30_000,
	});

	// PD-14691: order widgets require the order-history surface. Defensive —
	// the dashboard dispatch already skips mounting this card.
	if (!display.canViewOrderHistory) {
		return null;
	}

	// Skeleton-first (PD-14692): real header + shaped rows. isLoading is
	// false while the query is disabled, so anon visitors fall through.
	if (isLoading) {
		return (
			<div>
				<div className="flex items-center justify-between mb-2">
					<span className="text-sm font-semibold text-default">
						{t(title ?? 'Recent Orders')}
					</span>
				</div>
				<RecentListRowsSkeleton />
			</div>
		);
	}

	const orders = data?.data ?? [];
	if (orders.length === 0) {
		return null;
	}

	return (
		<div>
			<div className="flex items-center justify-between mb-2">
				<span className="text-sm font-semibold text-default">
					{t(title ?? 'Recent Orders')}
				</span>
				<button
					type="button"
					onClick={() => {
						void navigate('/orders');
					}}
					className="text-xs text-portal-accent hover:underline"
				>
					{t('View all')}
				</button>
			</div>
			<div className="flex flex-col gap-2">
				{orders.map((order) => (
					<button
						key={order.id}
						type="button"
						onClick={() => {
							void navigate(`/orders/${order.id}`);
						}}
						className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3 text-left hover:border-portal-accent transition-colors"
					>
						{/* flex-1 pins the total + chevron to the row's right edge
						    (PD-14241 alignment fix — see StatementsPage). */}
						<div className="flex-1 min-w-0">
							<div className="text-sm font-medium text-default truncate">
								{t('Order')} #
								{t(order.externalId2 ?? order.externalId ?? String(order.id))}
							</div>
							<div className="text-xs text-brand-muted mt-0.5">
								{formatStatementDate(order.date)}
								{' · '}
								{t(getOrderStatusLabel(order.status))}
							</div>
						</div>
						{/* PD-14239: hide the order total when this portal user
						    cannot see pricing; order/date/status stay visible. */}
						{display.canSeePricing && (
							<div className="text-sm font-semibold text-default shrink-0">
								{formatCurrency(order.total)}
							</div>
						)}
						<CardChevron />
					</button>
				))}
			</div>
		</div>
	);
}
